import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import {
  $createParagraphNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isNodeSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_LOW,
  COMMAND_PRIORITY_NORMAL,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ENTER_COMMAND,
} from "lexical";
import { uniq } from "lodash-es";
import { useCallback, useEffect } from "react";
import { notEmpty } from "../../../../../../../utils/not-empty";
import type { UUID } from "../../../../../../../utils/uuid";
import { useLexicalContext } from "../../../../lexical-context";
import { $createPollNode, $isPollNode } from "../../../../nodes/poll/helpers";
import {
  DELETE_NODE_COMMAND,
  INSERT_POLL_COMMAND,
  MODIFY_POLL_COMMAND,
  OPEN_EDIT_POLL_COMMAND,
} from "../../../../types/commands";
import { $findAllNodesOfType } from "../../../../utils/find-all-nodes-of-type";
import { $insertNode } from "../../../../utils/insert-node";

export function usePollButtonCommands(onChange: (poll_ids: UUID[]) => void): void {
  const [editor] = useLexicalComposerContext();
  const {
    editorModal: { handleOpen },
  } = useLexicalContext();

  const insertParagraphIfAtEnd = useCallback((event: KeyboardEvent) => {
    const selection = $getSelection();
    if (!$isNodeSelection(selection)) {
      return false;
    }

    const nodes = selection.getNodes();
    if (nodes.length !== 1) {
      return false;
    }

    const node = nodes[0];
    if (!$isPollNode(node)) {
      return false;
    }

    const noSiblingAfter = node.getNextSibling() === null;

    if (noSiblingAfter) {
      const newParagraph = $createParagraphNode();
      node.insertAfter(newParagraph);
      newParagraph.select();

      event.preventDefault();
      return true;
    }

    return false;
  }, []);

  const getPollIds = useCallback(
    (action: { type: "UPSERT"; poll_id: UUID } | { type: "DELETE"; nodeKey: string }): UUID[] => {
      return editor.getEditorState().read(() => {
        const baseIds = action.type === "UPSERT" ? [action.poll_id] : [];

        return uniq([
          ...baseIds,
          ...$findAllNodesOfType($getRoot(), $isPollNode)
            .map((node) => {
              if (action.type === "DELETE" && node.getKey() === action.nodeKey) {
                return null;
              }
              return node.getPollId();
            })
            .filter(notEmpty),
        ]);
      });
    },
    [editor],
  );

  useEffect(
    () =>
      mergeRegister(
        // Inserts a new PollNode into the document
        editor.registerCommand(
          INSERT_POLL_COMMAND,
          ({ poll_id, answer_type }) => {
            editor.update(
              () => {
                $insertNode($createPollNode(editor._headless, poll_id, answer_type));

                onChange(getPollIds({ type: "UPSERT", poll_id }));
              },
              { discrete: true },
            );
            return true;
          },
          COMMAND_PRIORITY_EDITOR,
        ),
        editor.registerCommand(
          OPEN_EDIT_POLL_COMMAND,
          ({ nodeKey }) => {
            handleOpen({ type: "poll", data: { mode: "edit", key: nodeKey } });
            return true;
          },
          COMMAND_PRIORITY_EDITOR,
        ),
        editor.registerCommand(
          MODIFY_POLL_COMMAND,
          ({ nodeKey, poll_id, answer_type }) => {
            editor.update(() => {
              const node = $getNodeByKey(nodeKey);

              if ($isPollNode(node)) {
                node.setAnswerType(answer_type);
                node.setPollId(poll_id);
                node.refresh();
              }

              onChange(getPollIds({ type: "UPSERT", poll_id }));
            });
            return true;
          },
          COMMAND_PRIORITY_EDITOR,
        ),
        editor.registerCommand(
          DELETE_NODE_COMMAND,
          (nodeKey) => {
            const node = $getNodeByKey(nodeKey);

            if ($isPollNode(node)) {
              onChange(getPollIds({ type: "DELETE", nodeKey }));
            }

            return false;
          },
          COMMAND_PRIORITY_NORMAL,
        ),
        editor.registerCommand(
          KEY_ARROW_DOWN_COMMAND,
          insertParagraphIfAtEnd,
          COMMAND_PRIORITY_LOW,
        ),
        editor.registerCommand(KEY_ENTER_COMMAND, insertParagraphIfAtEnd, COMMAND_PRIORITY_LOW),
      ),
    [editor, handleOpen, onChange, getPollIds, insertParagraphIfAtEnd],
  );
}
